<template>
  <div class="flex h-full flex-col">
    <div class="flex items-center justify-between border-b px-4 py-3">
      <h3 class="text-sm font-medium">{{ t('ai.conversations') }}</h3>
      <Button
        variant="ghost"
        size="sm"
        @click="aiStore.startNewConversation()"
      >
        <Plus class="mr-1 size-3" />
        {{ t('ai.newConversation') }}
      </Button>
    </div>

    <!-- Search bar -->
    <div
      v-if="!isLoading && conversations.length > 0"
      class="border-b px-4 py-2"
    >
      <div class="relative">
        <Search class="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          v-model="searchQuery"
          :placeholder="t('ai.searchConversations')"
          class="h-8 px-8 text-sm"
        />
        <button
          v-if="searchQuery"
          class="
            absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground
            hover:text-foreground
          "
          @click="searchQuery = ''"
        >
          <X class="size-3.5" />
        </button>
      </div>
    </div>

    <div
      v-if="isLoading"
      class="flex-1 overflow-y-auto"
    >
      <div
        v-for="i in 4"
        :key="i"
        class="border-b px-4 py-3"
      >
        <Skeleton class="mb-2 h-4 w-3/4" />
        <Skeleton class="h-3 w-1/3" />
      </div>
    </div>

    <div
      v-else-if="conversations.length === 0"
      class="flex flex-1 items-center justify-center p-4 text-center text-sm text-muted-foreground"
    >
      {{ t('ai.noConversations') }}
    </div>

    <!-- No search results -->
    <div
      v-else-if="filteredConversations.length === 0"
      class="flex flex-1 items-center justify-center p-4 text-center text-sm text-muted-foreground"
    >
      {{ t('ai.noSearchResults') }}
    </div>

    <div
      v-else
      class="flex-1 overflow-y-auto"
    >
      <div
        v-for="conv in filteredConversations"
        :key="conv.id"
        :class="[
          `
            group flex w-full items-center justify-between border-b px-4 py-3 text-left
            hover:bg-muted/50
          `,
          conv.id === aiStore.currentConversationId && 'bg-muted/50',
        ]"
      >
        <!-- Inline edit mode -->
        <div
          v-if="editingId === conv.id"
          class="flex min-w-0 flex-1 items-center gap-1"
        >
          <input
            ref="editInputRef"
            v-model="editTitle"
            class="h-7 min-w-0 flex-1 rounded border bg-background px-2 text-sm"
            @keydown.enter="saveEdit(conv.id)"
            @keydown.escape="cancelEdit"
            @blur="saveEdit(conv.id)"
          />
        </div>

        <!-- Normal display mode -->
        <button
          v-else
          class="min-w-0 flex-1 text-left"
          @click="loadConversation(conv.id)"
        >
          <p class="truncate text-sm font-medium">
            {{ conv.title || t('ai.untitledConversation') }}
          </p>
          <p class="text-xs text-muted-foreground">
            {{ formatDate(conv.updated_at) }}
          </p>
        </button>

        <div
          v-if="editingId !== conv.id"
          class="flex shrink-0 items-center"
        >
          <Button
            variant="ghost"
            size="icon"
            class="
              size-7 opacity-0
              group-hover:opacity-100
            "
            :title="t('ai.editTitle')"
            @click.stop="startEdit(conv)"
          >
            <Pencil class="size-3" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="
              size-7 opacity-0
              group-hover:opacity-100
            "
            @click.stop="deleteConversation(conv.id)"
          >
            <Trash2 class="size-3" />
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDebounceFn } from '@vueuse/core'

import { Pencil, Plus, Search, Trash2, X } from 'lucide-vue-next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import type { AiConversation } from '@/stores/ai'
import { useAiStore } from '@/stores/ai'

import { useAiConversations } from './useAiConversations'

const aiStore = useAiStore()
const { t } = useI18n()
const {
  conversations,
  isLoading,
  loadConversation,
  renameConversation,
  deleteConversation,
} = useAiConversations()

const searchQuery = ref('')
const debouncedQuery = ref('')
const updateSearch = useDebounceFn((val: string) => { debouncedQuery.value = val }, 300)
watch(searchQuery, (val) => updateSearch(val))

const filteredConversations = computed(() => {
  const q = debouncedQuery.value.toLowerCase().trim()
  if (!q) return conversations.value
  return conversations.value.filter(c => (c.title || '').toLowerCase().includes(q))
})

const editingId = ref<string | null>(null)
const editTitle = ref('')
const editInputRef = ref<HTMLInputElement[] | null>(null)

function startEdit(conv: AiConversation) {
  editingId.value = conv.id
  editTitle.value = conv.title || ''
  nextTick(() => {
    const inputs = editInputRef.value
    if (inputs && inputs.length > 0) {
      inputs[0].focus()
      inputs[0].select()
    }
  })
}

function cancelEdit() {
  editingId.value = null
  editTitle.value = ''
}

async function saveEdit(id: string) {
  const trimmed = editTitle.value.trim()
  if (!trimmed || editingId.value !== id) {
    cancelEdit()
    return
  }
  try {
    await renameConversation(id, trimmed)
  } catch {
    // Silently fail — the store already has the old title
  }
  editingId.value = null
  editTitle.value = ''
}

function formatDate(dateStr: string) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(new Date(dateStr))
  } catch {
    return dateStr
  }
}
</script>
